import React from 'react';
import { Input } from 'antd';
import { useHistory, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Title, Button, Form } from '~/components';
import paths from '~/pages/paths';
import { useExistingCompany, useValidateOtpInvitation } from '~/controllers/invitation';
import theme from '~/constants/theme';
import { ApiError } from '~/types/ApiError';

const SmsOtpPage: React.FC = () => {
  const history = useHistory();
  const params = useParams<{ smsOtpChallengeId: string }>();

  const [isLoading, setIsLoading] = React.useState(false);
  const { t } = useTranslation('ksaOnboarding');
  const validateOtp = useValidateOtpInvitation();
  const [, setLegalName] = useExistingCompany();

  const onSubmitCode = ({ code }: { code: string }) => {
    setIsLoading(true);
    validateOtp
      .mutateAsync({
        challengeId: params.smsOtpChallengeId,
        smsOtpCode: code,
      })
      .then(({ existingApplicationUid, existingCompanyLegalName }) => {
        if (existingApplicationUid) {
          history.push(paths.ksa.invitation.existingUser);
          setLegalName({ legalName: existingCompanyLegalName });
        } else {
          history.push(paths.ksa.invitation.letsStart);
        }
      })
      .catch((error: ApiError) => {
        history.push(
          paths.ksa.invitation.error.replace(':errorType', error.response?.data.code ?? 'default')
        );
      });
  };

  return (
    <Form name="invitationSmsOtpForm" onFinish={onSubmitCode}>
      <Title text={t('Get started')} subtext={t('with {{BRANDING_NAME}}')} />
      <p className="SmsOtpPage-InputHintParagraph">
        {t('Please enter the OTP sent to your phone number')}
      </p>
      <div>
        <Form.Item
          label={t('Verification code')}
          name="code"
          required
          rules={[{ required: true, message: t('Please enter SMS OTP') }]}
        >
          <InputValue name="code" disabled={isLoading} />
        </Form.Item>

        <ButtonContainer>
          <StyledButton
            id="invitationSmsOtpForm_resend"
            danger
            type="default"
            onClick={() => history.goBack()}
          >
            {t('Resend')}
          </StyledButton>
          <StyledButton
            id="invitationSmsOtpForm_submit"
            type="primary"
            htmlType="submit"
            loading={isLoading}
          >
            {t('Confirm')}
          </StyledButton>
        </ButtonContainer>
      </div>
    </Form>
  );
};

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  max-width: ${theme.formInputMaxWidth}px;
`;

const StyledButton = styled(Button)`
  margin-top: 8px;
  width: 48%;
`;

const InputValue = styled(Input)`
  &[dir='rtl'] {
    direction: ltr;
    text-align: right;
  }
`;

export default SmsOtpPage;
